import { DateInput, TextInput } from '@core/components/Inputs';
import CurrencyTextField from '@core/components/Inputs/CurrencyTextField';
import { SelectInput } from '@core/components/Select';
import { FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch, Tooltip } from '@material-ui/core';
import { cancelarAlert } from 'app/services/alerts';
import configuracaoBanco from 'app/utils/configuracaoBanco';
import { useFormikContext } from 'formik';
import React, { useContext, useMemo } from 'react';
import { ContaContext } from '../../../ContaContext';

const DEFINIR_PRINCIPAL = 'Deseja definir esta conta bancária como principal?';
const RETIRAR_PRINCIPAL = 'Deseja retirar esta conta bancária como principal?';

const EditarContaBancariaForm = () => {
  const formik = useFormikContext();

  const [bancos] = useContext(ContaContext).bancos;
  const [contasCorrente] = useContext(ContaContext).contasCorrente;
  const [contaAtual] = useContext(ContaContext).contaAtual;

  const { errors, touched, submitCount } = formik;

  const _errors = {
    descricao: (submitCount || touched?.descricao) && errors.descricao,
    contaBancaria: {
      tipoConta: (submitCount || touched?.contaBancaria?.tipoConta) && errors?.contaBancaria?.tipoConta,
      bancoId: (submitCount || touched?.contaBancaria?.bancoId) && errors?.contaBancaria?.bancoId,
      agencia: (submitCount || touched?.contaBancaria?.agencia) && errors?.contaBancaria?.agencia,
      agenciaDigito: (submitCount || touched?.contaBancaria?.agenciaDigito) && errors?.contaBancaria?.agenciaDigito,
      numeroConta: (submitCount || touched?.contaBancaria?.numeroConta) && errors?.contaBancaria?.numeroConta,
      contaDigito: (submitCount || touched?.contaBancaria?.contaDigito) && errors?.contaBancaria?.contaDigito,
      saldoInicial: (submitCount || touched?.contaBancaria?.saldoInicial) && errors?.contaBancaria?.saldoInicial,
      dataSaldo: (submitCount || touched?.contaBancaria?.dataSaldo) && errors?.contaBancaria?.dataSaldo,
      dataEncerramento:
        (submitCount || touched?.contaBancaria?.dataEncerramento) && errors?.contaBancaria?.dataEncerramento,
    },
  };

  const onlyNumberRegex = /^\d*$/;
  const alphaNumericRegex = /^[a-z0-9]+$/i;
  const codigoBanco = formik.values?.contaBancaria?.bancoCodigo;
  const maxLengthAgencia = configuracaoBanco.get(codigoBanco)?.agencia || configuracaoBanco.get('global')?.agencia;
  const maxLengthConta = configuracaoBanco.get(codigoBanco)?.conta || configuracaoBanco.get('global')?.conta;

  /*
   RNE-071 - Definição de conta principal : O comando "Definir como conta principal" 
   deve ser exibido marcado por padrão nos cadastros de contas correntes, até que tenha uma conta definida como principal. 
   Os próximos cadastros devem ser exibidos desmarcados por padrão, mas caso o usuário coloque outra conta como principal, 
   o sistema deve retirar o outro vínculo existente e permitir a alteração.
  */

  const inveterStatusAtual = () => {
    const isContaPrincipal = formik.values?.contaBancaria.principal;
    return !isContaPrincipal;
  };

  const trocarValor = () => {
    formik.setFieldValue('contaBancaria.principal', inveterStatusAtual());
  };

  const openModal = (event) => {
    const isDefinindo = inveterStatusAtual();
    const mensagemModal = isDefinindo ? DEFINIR_PRINCIPAL : RETIRAR_PRINCIPAL;

    cancelarAlert({
      opcoes: {
        title: '',
        text: mensagemModal,
      },
      confirmar: () => {
        trocarValor();
        formik.handleChange(event);
      },
    });
  };

  const habilitarContaPrincipalAutomatico = () => {
    if (!contaAtual?.id && formik.values.contaBancaria.tipoConta === 'CORRENTE') {
      if (contasCorrente.every((contaCorrente) => !contaCorrente.principal)) {
        formik.setFieldValue('contaBancaria.principal', true);
      }
    }
  };

  /*
   * RNE-060 - Limite de dígitos para "Agência" e "Conta" :
   * O sistema deve exibir inicialmente os campos "Agência" e "Conta" desabilitados até que o campo "Banco" seja informado.
   * Observação: Caso o usuário altere ou limpe o banco após ter informado os campos, o sistema deve limpá-los.
   */
  const desabilitarAgenciaConta = !formik.values?.contaBancaria?.bancoId;

  /*
   * RNE-060
   */
  const handleChangeBanco = (event) => {
    const [banco = {}] = bancos.filter(({ id }) => event.target.value === id);

    formik.setFieldValue('contaBancaria.agencia', '');
    formik.setFieldValue('contaBancaria.agenciaDigito', '');
    formik.setFieldValue('contaBancaria.numeroConta', '');
    formik.setFieldValue('contaBancaria.contaDigito', '');

    habilitarContaPrincipalAutomatico();

    formik.handleChange(event);

    formik.setFieldValue('contaBancaria.bancoCodigo', banco.codigo);
  };

  const handleChangeTipoConta = (event) => {
    if (event.target.value !== 'CORRENTE') {
      formik.setFieldValue('contaBancaria.principal', false);
    } else {
      habilitarContaPrincipalAutomatico();
    }
    formik.handleChange(event);
  };

  /*
    RNE-059 - Definição de conta principal: O sistema deve habilitar o toggle para definir a conta como principal somente quando o tipo da conta for corrente.
    Caso a conta seja principal e o tipo for alterado ou a conta for encerrada, o sistema deve retirar a característica de conta principal e desabilitar o toggle.
  */
  const handleChangeEncerrarConta = (event) => {
    if (event.target.value) {
      formik.setFieldValue('contaBancaria.principal', false);
      formik.setFieldValue('contaBancaria.dataEncerramento', null);
    }

    formik.handleChange(event);
  };

  const handleChangeDigito = (event) => {
    if (event.target.value?.length <= 1) {
      formik.handleChange(event);
    }
  };

  /*
    RNE-059 - Definição de conta principal: O sistema deve habilitar o toggle para definir a conta como principal
    somente quando o tipo da conta for corrente.
    Caso a conta seja principal e o tipo for alterado ou a conta for encerrada,
    o sistema deve retirar a característica de conta principal e desabilitar o toggle.
  */
  const desabilitarContaPrincipal = () => {
    return formik.values?.contaBancaria?.tipoConta !== 'CORRENTE' || formik.values?.contaBancaria?.encerrarConta;
  };

  return useMemo(
    () => (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
          <TextInput
            id="descricao"
            name="descricao"
            autoFocus
            onChange={formik.handleChange}
            onBlur={() => formik.handleBlur}
            value={formik.values.descricao}
            touched={formik.touched.descricao}
            error={_errors.descricao}
            required
            maxLength={30}
            fullWidth
            label="Descrição"
            warning={
              contaAtual.possuiBoleto &&
              'As alterações realizadas nos dados da conta serão aplicadas somente nos novos boletos e cobranças Pix gerados.'
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
          <FormControl variant="outlined" style={{ width: '100%' }} error={!!_errors.contaBancaria.tipoConta}>
            <InputLabel id="tipo-conta">Tipo da conta *</InputLabel>
            <Select
              value={formik.values?.contaBancaria?.tipoConta}
              onChange={handleChangeTipoConta}
              onBlur={formik.handleBlur}
              labelId="tipo-conta"
              id="contaBancaria.tipoConta"
              name="contaBancaria.tipoConta"
              label="Tipo da conta *"
            >
              <MenuItem value="CORRENTE">Conta Corrente</MenuItem>
              <MenuItem value="POUPANCA">Conta Poupança</MenuItem>
              <MenuItem value="INVESTIMENTO">Conta Investimento</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <SelectInput
            label="Banco"
            id="contaBancaria.bancoId"
            name="contaBancaria.bancoId"
            onChange={handleChangeBanco}
            onBlur={formik.handleBlur}
            value={formik.values?.contaBancaria?.bancoId || ''}
            error={_errors.contaBancaria.bancoId}
            touched={!!_errors.contaBancaria.bancoId}
            options={
              bancos
                ? bancos.map((banco) => ({
                    id: banco.id,
                    title: `${banco.codigo ? `${banco.codigo} - ` : ''}${banco.nome}`,
                  }))
                : []
            }
            required
          />
        </Grid>
        <Grid item xs={8} sm={8} md={4} lg={4} xl={4}>
          <TextInput
            disabled={desabilitarAgenciaConta}
            id="contaBancaria.agencia"
            name="contaBancaria.agencia"
            pattern={onlyNumberRegex}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values?.contaBancaria?.agencia}
            error={_errors.contaBancaria.agencia}
            touched={formik.touched?.contaBancaria?.agencia}
            maxLength={maxLengthAgencia}
            required
            fullWidth
            label="Agência"
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2} lg={2} xl={2}>
          <TextInput
            disabled={desabilitarAgenciaConta}
            id="contaBancaria.agenciaDigito"
            name="contaBancaria.agenciaDigito"
            pattern={alphaNumericRegex}
            value={formik.values?.contaBancaria?.agenciaDigito}
            onChange={handleChangeDigito}
            onBlur={formik.handleBlur}
            touched={formik.touched.contaBancaria?.agenciaDigito}
            error={_errors.contaBancaria.agenciaDigito}
            fullWidth
            label="Dígito"
          />
        </Grid>
        <Grid item xs={8} sm={8} md={4} lg={4} xl={4}>
          <TextInput
            disabled={desabilitarAgenciaConta}
            id="contaBancaria.numeroConta"
            name="contaBancaria.numeroConta"
            pattern={onlyNumberRegex}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.contaBancaria?.numeroConta}
            value={formik.values?.contaBancaria?.numeroConta}
            error={_errors.contaBancaria.numeroConta}
            maxLength={maxLengthConta}
            required
            fullWidth
            label="Conta"
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2} lg={2} xl={2}>
          <TextInput
            disabled={desabilitarAgenciaConta}
            id="contaBancaria.contaDigito"
            name="contaBancaria.contaDigito"
            pattern={alphaNumericRegex}
            onChange={handleChangeDigito}
            onBlur={formik.handleBlur}
            touched={formik.touched.contaBancaria?.contaDigito}
            value={formik.values?.contaBancaria?.contaDigito}
            error={_errors.contaBancaria.contaDigito}
            required
            fullWidth
            variant="outlined"
            label="Dígito"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
          <Tooltip
            placement="top-start"
            title="Saldo que o sistema irá considerar para cálculos automáticos conforme movimentações posteriores à data informada"
          >
            <div>
              <CurrencyTextField
                outputFormat="number"
                decimalCharacter=","
                digitGroupSeparator="."
                maximumValue="999999999"
                decimalPlaces={2}
                decimalPlacesShownOnBlur={2}
                decimalPlacesShownOnFocus={2}
                error={!!_errors.contaBancaria.saldoInicial}
                helperText={_errors.contaBancaria.saldoInicial || ''}
                onChange={(e, value) => {
                  formik.setFieldValue('contaBancaria.saldoInicial', value);
                }}
                onBlur={formik.handleBlur}
                id="contaBancaria.saldoInicial"
                name="contaBancaria.saldoInicial"
                value={formik.values?.contaBancaria?.saldoInicial}
                required
                fullWidth
                variant="outlined"
                label="Saldo inicial (R$)"
              />
            </div>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
          <DateInput
            fullWidth
            disableFuture
            id="contaBancaria.dataSaldo"
            name="contaBancaria.dataSaldo"
            label="Data do saldo *"
            variant="outlined"
            value={formik.values?.contaBancaria?.dataSaldo}
            error={!!_errors.contaBancaria.dataSaldo}
            helperText={_errors.contaBancaria.dataSaldo}
            touched={touched?.contaBancaria?.dataSaldo}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <FormControlLabel
            disabled={desabilitarContaPrincipal()}
            control={
              <Switch
                id="contaBancaria.principal"
                name="contaBancaria.principal"
                checked={formik.values?.contaBancaria?.principal}
                onChange={openModal}
                onBlur={formik.handleBlur}
                color="primary"
              />
            }
            label="Definir conta como principal"
          />
        </Grid>
        <Grid item xs={0} sm={0} md={6} lg={6} xl={6} />
        {contaAtual?.id && (
          <Grid item xs={6} sm={4} md={3} lg={3} xl={3}>
            <FormControlLabel
              control={
                <Switch
                  id="contaBancaria.encerrarConta"
                  name="contaBancaria.encerrarConta"
                  value={formik.values?.contaBancaria?.encerrarConta}
                  checked={formik.values?.contaBancaria?.encerrarConta}
                  onChange={handleChangeEncerrarConta}
                  onBlur={formik.handleBlur}
                  color="primary"
                />
              }
              label="Encerrar conta"
            />
          </Grid>
        )}
        {formik.values?.contaBancaria?.encerrarConta && (
          <Grid item xs={6} sm={4} md={3} lg={3} xl={3}>
            <DateInput
              fullWidth
              disableFuture
              id="contaBancaria.dataEncerramento"
              name="contaBancaria.dataEncerramento"
              label="Data do encerramento *"
              value={formik.values?.contaBancaria?.dataEncerramento}
              error={!!_errors.contaBancaria.dataEncerramento}
              helperText={_errors.contaBancaria.dataEncerramento || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        )}
      </Grid>
    ),
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bancos, formik]
  );
};

export default EditarContaBancariaForm;
